import { useMemo, useState } from 'react';
import { Box, Card, Flex, Heading, Text, Button, Link } from 'rebass';

const baseLabel = night => ({
  minWidth: 140,
  color: night ? '#cbd5e1' : '#6b7280',
  fontSize: 14
});

const baseValue = night => ({
  fontFamily: 'Menlo, ui-monospace, monospace',
  color: night ? '#f5f5f5' : '#0b1220',
  fontSize: 14,
  wordBreak: 'break-word'
});

function detectEngine(ua) {
  const isWebKit = /AppleWebKit\//.test(ua);
  const isGecko = /Gecko\//.test(ua) && !/like Gecko/.test(ua) && !isWebKit;
  const isEdgeHTML = /Edge\/\d+/.test(ua) && !/Edg\//.test(ua);
  const isBlink = !isWebKit && !isGecko && !isEdgeHTML;

  let webkitVersion = null;
  const m = ua.match(/AppleWebKit\/(\d+(?:\.\d+)*)/);
  if (m) webkitVersion = m[1];

  return { isWebKit, isBlink, isGecko, isEdgeHTML, webkitVersion };
}

function detectBrowser(ua) {
  // Very lightweight, best‑effort detection
  if (/Edg\//.test(ua)) return 'Microsoft Edge (Chromium)';
  if (/OPR\//.test(ua)) return 'Opera';
  if (/Chrome\//.test(ua) && /Safari\//.test(ua)) return 'Chrome';
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return 'Safari';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/MSIE|Trident\//.test(ua)) return 'Internet Explorer';
  return 'Unknown';
}

const row = (label, value, night) => (
  <Flex key={label} py={2} sx={{ borderTop: night ? '1px solid #2a2f35' : '1px solid #e5e7eb' }}>
    <Text sx={baseLabel(night)}>{label}</Text>
    <Text sx={baseValue(night)}>{value || '—'}</Text>
  </Flex>
);

export default function IpInfo({ night = false }) {
  const [ip, setIp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const vendor = typeof navigator !== 'undefined' ? navigator.vendor : '';
  const platform = typeof navigator !== 'undefined' ? navigator.platform : '';

  const brands = useMemo(() => {
    const d = typeof navigator !== 'undefined' ? navigator.userAgentData : undefined;
    if (d && Array.isArray(d.brands)) {
      return d.brands.map((b) => `${b.brand} ${b.version}`).join(', ');
    }
    return '';
  }, []);

  const engine = useMemo(() => detectEngine(ua), [ua]);
  const browser = useMemo(() => detectBrowser(ua), [ua]);

  const fetchIp = async () => {
    try {
      setLoading(true);
      setError('');
      // Public IP via ipify (simple, CORS‑enabled)
      const res = await fetch('https://api.ipify.org?format=json');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setIp(data.ip || '');
    } catch (e) {
      setError('Unable to fetch public IP');
    } finally {
      setLoading(false);
    }
  };

  // Fetch only after explicit user action for clearer consent.

  return (
    <Box mt={4}>
      <Card
        sx={{
          p: 3,
          borderRadius: 12,
          bg: night ? 'rgba(255,255,255,0.04)' : '#fff',
          color: night ? '#f5f5f5' : '#0b1220',
          border: night ? '1px solid #2a2f35' : '1px solid rgba(0,0,0,0.06)',
          boxShadow: night
            ? '0 2px 10px rgba(0,0,0,0.35)'
            : '0 1px 2px rgba(16,24,40,0.08)'
        }}
      >
        <Flex mb={2} alignItems="center" sx={{ justifyContent: 'space-between' }}>
          <Heading as="h3" fontSize={[2, 3]} sx={{ m: 0, fontFamily: 'Menlo, ui-monospace, monospace' }}>
            IP + WebKit Info
          </Heading>
          <Button
            onClick={fetchIp}
            disabled={loading}
            sx={{
              cursor: 'pointer',
              px: 3,
              py: 2,
              fontSize: 14,
              borderRadius: 9999,
              bg: night ? 'rgba(255,255,255,0.08)' : '#f3f4f6',
              color: night ? '#fff' : '#0b1220',
              boxShadow: night
                ? '0 1px 2px rgba(0,0,0,0.35)'
                : '0 1px 2px rgba(16,24,40,0.04)',
              '&:hover': {
                boxShadow: night
                  ? '0 8px 24px rgba(0,0,0,0.5)'
                  : '0 8px 24px rgba(16,24,40,0.08)'
              }
            }}
          >
            {loading ? 'Fetching…' : ip ? 'Refresh IP' : 'Fetch IP'}
          </Button>
        </Flex>

        <Text mb={2} sx={{ color: night ? '#cbd5e1' : '#6b7280', fontSize: 13 }}>
          To display your public IP, click “Fetch IP”. Your browser will contact ipify to retrieve it.
        </Text>

        {row('Public IP', error ? error : ip, night)}
        {row('Browser', browser, night)}
        {row('Engine',
          engine.isWebKit
            ? `WebKit${engine.webkitVersion ? ` ${engine.webkitVersion}` : ''}`
            : engine.isBlink
            ? 'Blink'
            : engine.isGecko
            ? 'Gecko'
            : engine.isEdgeHTML
            ? 'EdgeHTML'
            : 'Unknown'
        , night)}
        {brands ? row('UA Brands', brands, night) : null}
        {vendor ? row('Vendor', vendor, night) : null}
        {platform ? row('Platform', platform, night) : null}
        {row('User Agent', ua, night)}

        <Box mt={3} sx={{ p: 3, borderRadius: 10, bg: night ? 'rgba(255,255,255,0.03)' : '#f9fafb', border: night ? '1px solid #2a2f35' : '1px solid #e5e7eb' }}>
          <Heading as="h4" fontSize={[1, 2]} sx={{ m: 0, mb: 2, fontFamily: 'Menlo, ui-monospace, monospace' }}>
            Privacy Summary
          </Heading>
          <Text sx={{ color: night ? '#cbd5e1' : '#6b7280', fontSize: 13, mb: 3 }}>
            Optional feature. By clicking “Fetch IP”, you consent to contacting ipify.org to retrieve your public IP. We do not store this data.
          </Text>
          {row('Purpose', 'Show your public IP and local browser details', night)}
          {row('Data', 'Public IP (on click) and request metadata to ipify; local UA/engine shown only in your browser', night)}
          {row('Recipient', <Link as="a" href="https://www.ipify.org/" target="_blank" rel="noopener noreferrer" sx={{ color: night ? '#93c5fd' : '#2563eb' }}>ipify.org</Link>, night)}
          {row('Transfers', 'Possible processing outside EU/EEA by ipify', night)}
          {row('Retention', 'We store nothing; see ipify policy for theirs', night)}
        </Box>
      </Card>
    </Box>
  );
}
